import type {
  ConstrainActionType,
  ShareAction,
  ShareActionArgs,
  ShareActionDefs,
} from '../types'

type CopyAction<K extends string> = ConstrainActionType<
  ShareAction,
  `copy_${K}`
>
type DownloadAction<K extends string> = ConstrainActionType<
  ShareAction,
  `download_${K}` | 'export_longform'
>

const ShareActionFactory = {
  share: {
    type: 'action',
    label: 'Share Content',
    actionType: 'share',
  },
  email: {
    type: 'action',
    label: 'Email Content',
    actionType: 'email',
  },
  download: ({ items = [] }) => ({
    type: 'submenu',
    label: 'Download',
    actionType: 'download',
    items,
  }),
  download_rendering: ({ disabled }) => ({
    type: 'action',
    label: 'Rendering',
    actionType: 'download_rendering',
    disabled,
  }),
  download_assets: ({ disabled }) => ({
    type: 'action',
    label: 'Assets',
    actionType: 'download_assets',
    disabled,
  }),
  export_longform: {
    type: 'action',
    label: 'Longform Export',
    actionType: 'export_longform',
  },
  download_csv: ({ disabled }) => ({
    type: 'action',
    label: 'Content CSV',
    actionType: 'download_csv',
    disabled,
  }),
  download_activities_csv: {
    type: 'action',
    label: 'Activities CSV',
    actionType: 'download_activities_csv',
  },
  copy: ({ items = [] }) => ({
    type: 'submenu',
    label: 'Copy',
    actionType: 'copy',
    items,
  }),
  copy_text: ({ disabled }) => ({
    type: 'action',
    label: 'Text',
    actionType: 'copy_text',
    disabled,
  }),
  copy_preview_url: ({ disabled }) => ({
    type: 'action',
    label: 'Preview URL',
    actionType: 'copy_preview_url',
    disabled,
  }),
} as const satisfies ShareActionDefs

export const getShareActions = ({
  assetDownloadEnabled,
  bulkDownloadEnabled,
  contentDownloadEnabled,
  contentTextCopyEnabled,
  exportEnabled,
  postType,
  previewUrlEnabled,
  renderingEnabled,
}: ShareActionArgs): ShareAction[] => {
  const shareActions: ShareAction[] = [
    ShareActionFactory.share,
    ShareActionFactory.email,
  ]
  const copyActions: CopyAction<string>[] = []
  const downloadActions: DownloadAction<string>[] = [
    ShareActionFactory.download_rendering({ disabled: !renderingEnabled }),
  ]

  if (exportEnabled) {
    const exportAction =
      postType === 'long_form'
        ? ShareActionFactory.export_longform
        : ShareActionFactory.download_csv({ disabled: !contentDownloadEnabled })

    downloadActions.unshift(exportAction)
    downloadActions.unshift(ShareActionFactory.download_activities_csv)
  }

  if (bulkDownloadEnabled) {
    downloadActions.unshift(
      ShareActionFactory.download_assets({ disabled: !assetDownloadEnabled })
    )
  }

  shareActions.push(ShareActionFactory.download({ items: downloadActions }))

  if (postType !== 'document' && postType !== 'linkedin_article') {
    copyActions.push(
      ShareActionFactory.copy_text({ disabled: !contentTextCopyEnabled })
    )
  }

  if (postType === 'url_preview') {
    copyActions.push(
      ShareActionFactory.copy_preview_url({ disabled: !previewUrlEnabled })
    )
  }

  if (copyActions.length > 0) {
    shareActions.push(ShareActionFactory.copy({ items: copyActions }))
  }

  return shareActions
}
